/**
 * One order-scoped cart-clearing intent. We only save the order ID and time;
 * never copy cart contents or payment details into this storage entry.
 *
 * This marker does NOT prove payment success. The checkout success screen must
 * validate the paid status through the backend before clearing Redux cart.
 */
const KEY = "pickdengu:cart:payment-awaiting-confirmation:v1";
const TTL_MS = 24 * 60 * 60 * 1000;

type PendingCartPayment = {
  version: 1;
  orderId: string;
  expiresAt: number;
};

const available = () => typeof window !== "undefined";

export function rememberCartCheckoutPayment(orderId: string) {
  if (!available()) return;
  const safeId = String(orderId || "").trim();
  if (!safeId) return;
  try {
    const marker: PendingCartPayment = {
      version: 1,
      orderId: safeId,
      expiresAt: Date.now() + TTL_MS,
    };
    window.sessionStorage.setItem(KEY, JSON.stringify(marker));
  } catch {
    // Do not break checkout when browser storage is unavailable.
  }
}

export function getCartCheckoutPayment(): PendingCartPayment | null {
  if (!available()) return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const marker = JSON.parse(raw) as PendingCartPayment;
    if (
      marker?.version === 1 &&
      typeof marker.orderId === "string" &&
      Boolean(marker.orderId) &&
      Number(marker.expiresAt) > Date.now()
    ) return marker;
  } catch {
    // Discard malformed or inaccessible browser storage.
  }
  forgetCartCheckoutPayment();
  return null;
}

export function forgetCartCheckoutPayment(orderId?: string) {
  if (!available()) return;
  try {
    if (orderId) {
      const raw = window.sessionStorage.getItem(KEY);
      if (!raw) return;
      const marker = JSON.parse(raw) as PendingCartPayment;
      if (marker?.orderId !== orderId) return;
    }
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Do not interrupt paid checkout due to storage restrictions.
  }
}
